import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import './css/FaqPage.css';

type FaqItemDef = {
  id: string;
  qKey: string;
  aKey: string;
};

type FaqSectionDef = {
  id: string;
  titleKey: string;
  items: FaqItemDef[];
};

const FAQ_SECTIONS: FaqSectionDef[] = [
  {
    id: 'general',
    titleKey: 'faq.catGeneral',
    items: [
      { id: 'what', qKey: 'faq.general.what.q', aKey: 'faq.general.what.a' },
      { id: 'free', qKey: 'faq.general.free.q', aKey: 'faq.general.free.a' },
      { id: 'topics', qKey: 'faq.general.topics.q', aKey: 'faq.general.topics.a' },
    ],
  },
  {
    id: 'account',
    titleKey: 'faq.catAccount',
    items: [
      { id: 'register', qKey: 'faq.account.register.q', aKey: 'faq.account.register.a' },
      { id: 'email', qKey: 'faq.account.email.q', aKey: 'faq.account.email.a' },
      { id: 'profile', qKey: 'faq.account.profile.q', aKey: 'faq.account.profile.a' },
    ],
  },
  {
    id: 'problems',
    titleKey: 'faq.catProblems',
    items: [
      { id: 'check', qKey: 'faq.problems.check.q', aKey: 'faq.problems.check.a' },
      { id: 'languages', qKey: 'faq.problems.languages.q', aKey: 'faq.problems.languages.a' },
      { id: 'hints', qKey: 'faq.problems.hints.q', aKey: 'faq.problems.hints.a' },
      { id: 'stuck', qKey: 'faq.problems.stuck.q', aKey: 'faq.problems.stuck.a' },
    ],
  },
  {
    id: 'premium',
    titleKey: 'faq.catPremium',
    items: [
      { id: 'what', qKey: 'faq.premium.what.q', aKey: 'faq.premium.what.a' },
      { id: 'subscribe', qKey: 'faq.premium.subscribe.q', aKey: 'faq.premium.subscribe.a' },
    ],
  },
  {
    id: 'classes',
    titleKey: 'faq.catClasses',
    items: [
      { id: 'what', qKey: 'faq.classes.what.q', aKey: 'faq.classes.what.a' },
      { id: 'join', qKey: 'faq.classes.join.q', aKey: 'faq.classes.join.a' },
    ],
  },
];

function FaqItem({
  item,
  open,
  onToggle,
}: {
  item: FaqItemDef;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <li className={`faq-page__item${open ? ' faq-page__item--open' : ''}`}>
      <button
        type="button"
        className="faq-page__question"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>{t(item.qKey)}</span>
        <span className="faq-page__chevron" aria-hidden>
          ▼
        </span>
      </button>
      {open ? <p className="faq-page__answer">{t(item.aKey)}</p> : null}
    </li>
  );
}

export default function FaqPage() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>('general-what');

  const toggle = (fullId: string) => {
    setOpenId((prev) => (prev === fullId ? null : fullId));
  };

  return (
    <div className="faq-page">
      <div className="faq-page__container">
        <header className="faq-page__header">
          <p className="faq-page__eyebrow">{t('faq.eyebrow')}</p>
          <h1 className="faq-page__title">{t('faq.title')}</h1>
          <p className="faq-page__subtitle">{t('faq.subtitle')}</p>
        </header>

        {FAQ_SECTIONS.map((section) => (
          <section key={section.id} className="faq-page__section" aria-labelledby={`faq-${section.id}`}>
            <h2 id={`faq-${section.id}`} className="faq-page__section-title">
              {t(section.titleKey)}
            </h2>
            <ul className="faq-page__list">
              {section.items.map((item) => {
                const fullId = `${section.id}-${item.id}`;
                return (
                  <FaqItem
                    key={fullId}
                    item={item}
                    open={openId === fullId}
                    onToggle={() => toggle(fullId)}
                  />
                );
              })}
            </ul>
          </section>
        ))}

        <aside className="faq-page__cta">
          <h2 className="faq-page__cta-title">{t('faq.ctaTitle')}</h2>
          <p className="faq-page__cta-desc">{t('faq.ctaDesc')}</p>
          <div className="faq-page__cta-actions">
            <a href="mailto:info@sciplex.com" className="faq-page__btn faq-page__btn--primary">
              {t('faq.ctaEmail')}
            </a>
            <Link to="/problems" className="faq-page__btn faq-page__btn--ghost">
              {t('faq.ctaProblems')}
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
